import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Trash2, Play, BookOpen, Maximize2, Minimize2 } from "lucide-react";
import Field from "@/shared/components/field";
import { renderNodeIcon, getNodeSolidColor } from "./nodeIcons";
import NodePropertiesForm from "./NodePropertiesForm";
import SwitchNodeEditor from "./SwitchNodeEditor";
import HttpRequestNodeEditor from "./HttpRequestNodeEditor";
import TransformNodeEditor from "./TransformNodeEditor";
import AvailableDataPanel from "./AvailableDataPanel";
import type WorkflowNode from "../../models/WorkflowNode";
import type NodeDescriptor from "../../models/NodeDescriptor";
import type { LastExecutionData } from "../../hooks/useLastExecutionData";

interface NodeConfigPanelProps {
  node: WorkflowNode;
  descriptor?: NodeDescriptor;
  isEntryNode: boolean;
  /** Muestras de datos de la última ejecución para el picker de expresiones. */
  executionData?: {
    data: LastExecutionData | null;
    loading: boolean;
    refresh: () => void;
  };
  /** id → nombre visible de los nodos del canvas (para etiquetar los datos). */
  nodeNames?: Record<string, string>;
  /** Nodo aguas arriba (edge Normal entrante): fuente de los campos de un Switch. */
  upstreamNodeId?: string;
  onChange: (node: WorkflowNode) => void;
  onDelete: () => void;
  onClose: () => void;
}

export default function NodeConfigPanel({
  node,
  descriptor,
  isEntryNode,
  executionData,
  nodeNames,
  upstreamNodeId,
  onChange,
  onDelete,
  onClose,
}: NodeConfigPanelProps) {
  const { bg } = getNodeSolidColor(node.type);
  const iconUrl = descriptor?.icon;
  const [expanded, setExpanded] = useState(false);

  const icon = (
    <span
      className={`flex size-9 shrink-0 items-center justify-center rounded-xl shadow-sm ${
        iconUrl ? "bg-white border border-black/5" : bg
      }`}
    >
      {renderNodeIcon(node.type, iconUrl ? "size-5" : "size-5 text-white", iconUrl)}
    </span>
  );

  const title = (
    <div className="min-w-0 flex-1">
      <h2 className="text-sm font-bold text-foreground truncate">{node.name || "Sin nombre"}</h2>
      <p className="text-[11px] text-foreground/40 font-medium truncate">
        {descriptor?.displayName ?? node.type}
      </p>
    </div>
  );

  const expandButton = (
    <button
      onClick={() => setExpanded((e) => !e)}
      className="flex items-center gap-1 shrink-0 px-2 py-1.5 rounded-xl text-[11px] font-semibold text-foreground/45 hover:bg-black/5 hover:text-foreground transition-colors cursor-pointer"
      title={expanded ? "Volver al panel lateral" : "Editar en pantalla grande"}
    >
      {expanded ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
      {expanded ? "Contraer" : "Expandir"}
    </button>
  );

  const identity = (
    <>
      {isEntryNode && (
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-green-600 bg-green-500/10 border border-green-500/20 px-2.5 py-1 rounded-full">
          <Play className="size-3" />
          Nodo de inicio (trigger manual)
        </span>
      )}

      <Field
        label="Nombre"
        name="name"
        value={node.name}
        onChange={(e) => onChange({ ...node, name: e.target.value })}
      />

      {descriptor?.description && (
        <p className="text-xs text-foreground/50 leading-relaxed">{descriptor.description}</p>
      )}
    </>
  );

  const dataPanel = executionData && (
    <AvailableDataPanel
      data={executionData.data}
      loading={executionData.loading}
      onRefresh={executionData.refresh}
      nodeNames={nodeNames ?? {}}
      excludeNodeId={node.id}
      relativeForNodeId={node.type === "Switch" ? upstreamNodeId : undefined}
      syntax={node.type === "Transform" ? "javascript" : "expression"}
    />
  );

  const properties = (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-foreground/40 mb-3">Propiedades</p>
      {node.type === "Switch" ? (
        <SwitchNodeEditor
          value={node.properties}
          onChange={(properties) => onChange({ ...node, properties })}
        />
      ) : node.type === "HttpRequest" ? (
        <HttpRequestNodeEditor
          value={node.properties}
          onChange={(properties) => onChange({ ...node, properties })}
        />
      ) : node.type === "Transform" ? (
        <TransformNodeEditor
          value={node.properties}
          onChange={(properties) => onChange({ ...node, properties })}
          nodeNames={nodeNames}
          currentNodeId={node.id}
          expanded={expanded}
        />
      ) : descriptor ? (
        <NodePropertiesForm
          schema={descriptor.propertiesSchema}
          value={node.properties}
          onChange={(properties) => onChange({ ...node, properties })}
        />
      ) : (
        <p className="text-sm text-foreground/40">
          No se encontró el esquema para este tipo de nodo.
        </p>
      )}
    </div>
  );

  const setupInstructions = descriptor?.setupInstructions && (
    <div className="border border-blue-500/20 bg-blue-500/5 rounded-xl p-3 space-y-2">
      <p className="text-[11px] font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1.5">
        <BookOpen className="size-3" />
        Pasos de configuración
      </p>
      <p className="text-xs text-foreground/60 leading-relaxed whitespace-pre-wrap">
        {descriptor.setupInstructions}
      </p>
    </div>
  );

  const deleteButton = (
    <button
      onClick={onDelete}
      className="w-full h-10 rounded-xl border border-red-500/20 bg-red-500/5 text-red-600 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-red-500 hover:border-red-500 hover:text-white transition-all cursor-pointer"
    >
      <Trash2 className="size-4" />
      Eliminar nodo
    </button>
  );

  return (
    <>
      <aside className="w-96 shrink-0 border-l border-black/5 bg-white/70 backdrop-blur-md overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center gap-3 px-5 py-3.5 bg-white/85 backdrop-blur-md border-b border-black/5">
          {icon}
          {title}
          {expandButton}
          <button
            onClick={onClose}
            className="flex size-8 shrink-0 items-center justify-center rounded-xl text-foreground/40 hover:bg-black/5 hover:text-foreground transition-colors cursor-pointer"
            aria-label="Cerrar"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Con el panel expandido el contenido vive en el modal; acá queda solo el marcador. */}
        {expanded ? (
          <div className="p-5">
            <div className="rounded-xl border border-dashed border-black/10 bg-black/[0.02] px-4 py-6 text-center">
              <p className="text-xs text-foreground/40">Editando en pantalla grande…</p>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {identity}
            {dataPanel}
            {properties}
            {setupInstructions}
            {deleteButton}
          </div>
        )}
      </aside>

      <AnimatePresence>
        {expanded && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setExpanded(false)}
              className="absolute inset-0 bg-black/40 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.98, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 12 }}
              className="relative z-10 flex w-full max-w-6xl max-h-[90vh] flex-col overflow-hidden rounded-3xl border border-black/5 bg-white/90 backdrop-blur-2xl shadow-2xl"
            >
              <div className="flex items-center gap-3 px-5 py-3.5 border-b border-black/5">
                {icon}
                {title}
                {expandButton}
              </div>

              <div className="flex min-h-0 flex-1 overflow-hidden">
                <div className="min-w-0 flex-1 overflow-auto p-6 space-y-5">
                  {identity}
                  {properties}
                  {setupInstructions}
                </div>
                {dataPanel && (
                  <div className="w-80 shrink-0 overflow-auto border-l border-black/5 bg-black/[0.02] p-4">
                    {dataPanel}
                  </div>
                )}
              </div>

              <div className="border-t border-black/5 px-5 py-3">{deleteButton}</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
